import { getRiskCategory, calculateCompositeRisk, getRiskColor } from '../utils/riskCalculations.js';

class RiskDataService {
  constructor() {
    this.riskData = null;
    this.loaded = false;
    this.loadingPromise = null;
  }

  async loadRiskData() {
    // Return existing promise if already loading
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Return data if already loaded
    if (this.loaded) {
      return this.riskData;
    }

    // Start loading
    this.loadingPromise = this._loadFromCSV();

    try {
      this.riskData = await this.loadingPromise;
      this.loaded = true;
      console.log('✅ Risk data loaded successfully:', this.riskData.size, 'countries');
      return this.riskData;
    } catch (error) {
      console.error('❌ Failed to load risk data:', error);
      this.loadingPromise = null;
      throw new Error('Unable to load fragile states index');
    }
  }

  async _loadFromCSV() {
    try {
      // Try to load from the CSV file first
      const response = await fetch('/FragileStatesIndex.csv');
      if (response.ok) {
        const text = await response.text();
        return this._parseCSVData(text);
      }
    } catch (error) {
      console.warn('⚠️ Could not load CSV file, using embedded data:', error);
    }

    // Fallback to embedded data
    return this._loadEmbeddedData();
  }

  _parseCSVData(csvText) {
    const riskMap = new Map();
    const lines = csvText.split('\n');

    // This CSV has a multi-row header structure:
    // Row 0: Categories
    // Row 1: Sub-headers (includes "Total")
    // Row 2: Years
    // Row 3+: Data starting with "Country"

    if (lines.length < 4) {
      throw new Error('CSV file does not have expected structure');
    }

    // The data columns are: Country, Rank, Rank, Total, [other columns...]
    const countryIndex = 0;  // "Country" is always first column in data rows
    const scoreIndex = 3;    // "Total" score is always 4th column (index 3)
    const rankIndex = 1;     // "Rank" is always 2nd column

    // Parse data rows (starting from row 3, which has actual data)
    for (let i = 3; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const columns = line.split(',');
      if (columns.length < Math.max(countryIndex, scoreIndex) + 1) continue;

      const country = columns[countryIndex].replace(/"/g, '').trim();
      const scoreStr = columns[scoreIndex].replace(/"/g, '').trim();
      const rankStr = rankIndex >= 0 ? columns[rankIndex].replace(/"/g, '').trim() : '';

      const score = parseFloat(scoreStr);
      if (isNaN(score) || !country) continue;

      const rank = rankStr ? parseInt(rankStr) : null;

      riskMap.set(country.toLowerCase(), {
        country,
        score,
        rank,
        category: getRiskCategory(score),
        lastUpdated: new Date().toISOString()
      });
    }

    return riskMap;
  }

  _loadEmbeddedData() {
    // Embedded FSI data as fallback (from the legacy fragileStatesIndex.js)
    const embeddedData = [
      ['Somalia', 111.3], ['Sudan', 109.3], ['South Sudan', 109.0], ['Syria', 108.1],
      ['Congo Democratic Republic', 106.7], ['Yemen', 106.6], ['Afghanistan', 103.9],
      ['Central African Republic', 103.9], ['Haiti', 103.5], ['Chad', 102.7],
      ['Myanmar', 100.0], ['Ethiopia', 98.1], ['Palestine', 97.8], ['Mali', 97.3],
      ['Nigeria', 96.6], ['Libya', 96.5], ['Guinea', 96.4], ['Zimbabwe', 95.7],
      ['Niger', 95.2], ['Cameroon', 94.3], ['Burkina Faso', 94.2], ['Ukraine', 93.1],
      ['Lebanon', 92.7], ['Burundi', 92.6], ['Mozambique', 92.5], ['Eritrea', 92.1],
      ['Pakistan', 91.7], ['Uganda', 91.1], ['Congo Republic', 90.2], ['Venezuela', 89.0],
      ['Iraq', 88.6], ['Guinea Bissau', 88.4], ['Sri Lanka', 88.2], ['Mauritania', 87.0],
      ['Liberia', 86.9], ['Kenya', 86.5], ['Bangladesh', 85.9], ['Angola', 85.6],
      ['Cote d\'Ivoire', 85.3], ['North Korea', 84.9], ['Turkey', 84.0],
      ['Equatorial Guinea', 83.7], ['Iran', 82.9], ['Egypt', 82.8], ['Sierra Leone', 82.6],
      ['Rwanda', 81.8], ['Comoros', 81.7], ['Djibouti', 81.6], ['Russia', 81.6],
      ['Zambia', 81.2], ['Togo', 81.1], ['Malawi', 80.5], ['Madagascar', 79.8],
      ['Papua New Guinea', 78.8], ['Cambodia', 78.6], ['Honduras', 78.1], ['Nepal', 78.0],
      ['Eswatini', 77.6], ['Solomon Islands', 77.6], ['Nicaragua', 76.7], ['Gambia', 76.1],
      ['Tanzania', 75.7], ['Colombia', 75.6], ['Philippines', 75.1], ['Guatemala', 74.9],
      ['Kyrgyz Republic', 74.9], ['Timor-Leste', 74.8], ['Lesotho', 74.6],
      ['Jordan', 74.3], ['Senegal', 74.2], ['Laos', 73.8], ['Azerbaijan', 72.8],
      ['Tajikistan', 72.8], ['Benin', 72.5], ['India', 72.3], ['Peru', 72.0],
      ['Bosnia and Herzegovina', 71.0], ['Brazil', 70.3], ['Gabon', 70.2],
      ['South Africa', 69.6], ['Bolivia', 69.4], ['Georgia', 69.3], ['Mexico', 69.0],
      ['Morocco', 68.8], ['Belarus', 68.7], ['El Salvador', 68.7], ['Algeria', 68.6],
      ['Sao Tome and Principe', 68.5], ['Armenia', 68.1], ['Ecuador', 68.0],
      ['Serbia', 67.8], ['Tunisia', 67.2], ['Micronesia', 66.9], ['Fiji', 66.4],
      ['Thailand', 66.2], ['Uzbekistan', 64.8], ['Moldova', 64.7], ['Bhutan', 64.5],
      ['China', 64.4], ['Bahrain', 64.2], ['Samoa', 63.9], ['Indonesia', 63.7],
      ['Saudi Arabia', 63.2], ['Turkmenistan', 62.2], ['Paraguay', 61.5], ['Ghana', 60.8],
      ['Maldives', 60.3], ['Dominican Republic', 60.2], ['Jamaica', 59.3], ['Namibia', 59.3],
      ['Guyana', 59.2], ['Cuba', 59.1], ['Suriname', 58.8], ['Macedonia', 58.1],
      ['Kazakhstan', 57.8], ['Cape Verde', 57.2], ['Belize', 57.0], ['Montenegro', 56.9],
      ['Vietnam', 56.2], ['Albania', 55.9], ['Greece', 54.7], ['Cyprus', 54.1],
      ['Brunei Darussalam', 53.9], ['Botswana', 53.6], ['Trinidad and Tobago', 53.5],
      ['Malaysia', 53.1], ['Antigua and Barbuda', 51.9], ['Grenada', 51.9],
      ['Israel', 51.5], ['Romania', 51.0], ['Seychelles', 51.0], ['Mongolia', 50.7],
      ['Bulgaria', 49.4], ['Kuwait', 49.3], ['Bahamas', 48.0], ['Panama', 47.7],
      ['Oman', 47.4], ['Hungary', 46.2], ['Croatia', 45.9], ['Barbados', 44.7],
      ['United States', 44.5], ['Argentina', 44.2], ['Spain', 44.0], ['Poland', 41.7],
      ['Latvia', 41.4], ['Chile', 41.1], ['Italy', 41.1], ['United Kingdom', 40.8],
      ['Qatar', 39.8], ['Costa Rica', 39.4], ['Mauritius', 37.8], ['Czech Republic', 37.7],
      ['Lithuania', 37.4], ['Estonia', 36.5], ['Slovak Republic', 35.3],
      ['United Arab Emirates', 34.7], ['Uruguay', 33.7], ['Malta', 31.1], ['Belgium', 30.3],
      ['Japan', 30.2], ['South Korea', 29.8], ['France', 28.3], ['Slovenia', 26.1],
      ['Portugal', 25.9], ['Singapore', 25.4], ['Germany', 24.0], ['Austria', 23.1],
      ['Sweden', 20.6], ['Australia', 19.6], ['Netherlands', 19.5], ['Luxembourg', 18.7],
      ['Canada', 18.6], ['Ireland', 18.6], ['Switzerland', 16.2], ['Denmark', 15.9],
      ['New Zealand', 15.9], ['Iceland', 15.2], ['Finland', 14.3], ['Norway', 12.7]
    ];

    const riskMap = new Map();
    embeddedData.forEach(([countryName, totalScore]) => {
      riskMap.set(countryName.toLowerCase(), {
        country: countryName,
        score: totalScore,
        rank: null, // No rank in embedded data
        category: getRiskCategory(totalScore),
        lastUpdated: new Date().toISOString()
      });
    });

    return riskMap;
  }

  getRiskForCountry(countryName) {
    if (!this.loaded) {
      console.warn('Risk data not loaded yet');
      return null;
    }

    if (!countryName || typeof countryName !== 'string') return null;

    // Try exact match first
    const key = countryName.toLowerCase();
    let riskData = this.riskData.get(key);

    if (!riskData) {
      // Try fuzzy matching for common variations
      riskData = this._fuzzyMatchCountry(countryName);
    }

    return riskData || null;
  }

  _fuzzyMatchCountry(countryName) {
    const searchKey = countryName.toLowerCase();

    // Common country name variations
    const variations = new Map([
      ['usa', 'united states'],
      ['uk', 'united kingdom'],
      ['uae', 'united arab emirates'],
      ['drc', 'congo democratic republic'],
      ['congo (drc)', 'congo democratic republic'],
      ['congo dr', 'congo democratic republic'],
      ['north macedonia', 'macedonia'],
      ['czech republic', 'czech republic'],
      ['south korea', 'south korea'],
      ['north korea', 'north korea']
    ]);

    // Check variations
    if (variations.has(searchKey)) {
      return this.riskData.get(variations.get(searchKey));
    }

    // Try partial matches
    for (const [key, data] of this.riskData.entries()) {
      if (key.includes(searchKey) || searchKey.includes(key)) {
        return data;
      }
    }

    return null;
  }

  enrichMineWithRisk(mine) {
    if (!mine || !mine.country) return mine;

    const countryRisk = this.getRiskForCountry(mine.country);
    if (!countryRisk) return mine;

    // Calculate composite risk score
    const compositeRisk = calculateCompositeRisk(mine, countryRisk);

    return {
      ...mine,
      risk_score: compositeRisk,
      risk_category: getRiskCategory(compositeRisk),
      risk_rank: countryRisk.rank,
      country_risk_score: countryRisk.score,
      country_risk_category: countryRisk.category,
      risk_color: getRiskColor(compositeRisk)
    };
  }

  enrichMinesArray(mines) {
    if (!Array.isArray(mines)) return mines;

    return mines.map(mine => this.enrichMineWithRisk(mine));
  }

  getCountryRiskStats() {
    if (!this.loaded) return null;

    const stats = {
      total: this.riskData.size,
      byCategory: {
        'very-high': 0,
        'high': 0,
        'medium': 0,
        'low': 0,
        'very-low': 0
      },
      averageScore: 0,
      lastUpdated: new Date().toISOString()
    };

    let totalScore = 0;
    for (const [, data] of this.riskData) {
      stats.byCategory[data.category]++;
      totalScore += data.score;
    }

    stats.averageScore = totalScore / stats.total;

    return stats;
  }

  getHighestRiskCountries(limit = 10) {
    if (!this.loaded) return [];

    return Array.from(this.riskData.values())
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);
  }

  getLowestRiskCountries(limit = 10) {
    if (!this.loaded) return [];

    return Array.from(this.riskData.values())
      .sort((a, b) => a.score - b.score)
      .slice(0, limit);
  }

  isLoaded() {
    return this.loaded;
  }

  getDataSize() {
    return this.riskData ? this.riskData.size : 0;
  }

  // Debugging method
  getAllCountries() {
    if (!this.loaded) return [];
    return Array.from(this.riskData.keys());
  }
}

// Export singleton instance
export const riskDataService = new RiskDataService();